import React from 'react'
import PropTypes from 'prop-types'
import { Switch, Route as ReactRouterRoute, Redirect } from 'react-router'
import { NotFoundView } from 'src/components'
import { BreadcrumbsContext } from 'src/contexts'
import { LoadingView } from 'src/components/views'
import isEqual from 'lodash/isEqual'
import { useRouter, useIsMounted, usePrevious } from '../../../hooks'

/**
 * Necessary wrapper used to set router context for children routes
 * @param {*} param0
 */
function RouteWrapper({ path, ...props }) {
  return (
    <ReactRouterRoute path={path}>
      <Route {...props} />
    </ReactRouterRoute>
  )
}

RouteWrapper.propTypes = {
  /**
   * The relative path of the route.
   * If empty, takes the same path as its parent's route.
   */
  path: PropTypes.string,
}

/**
 * Route component handling nested routes and breadcrumbs
 * @param {*} props
 */
function Route(props) {
  const { children, component, breadcrumb, guard, redirectTo } = props

  // get router current match
  const {
    match: { url, params, path },
    location,
  } = useRouter()

  // check guard on guard and params change
  const [{ checking, authorized, error }, setState] = React.useState({ authorized: !guard, checking: !!guard })

  // get current mount status
  const isMounted = useIsMounted()

  // keep trace of previous props
  const previousGuard = usePrevious(guard)
  const previousParams = usePrevious(params)

  /* eslint-disable react-hooks/exhaustive-deps */

  // execute the guard if it has changed or if route params have changed
  React.useEffect(() => {
    if (guard && (guard !== previousGuard || !isEqual(params, previousParams))) {
      setState({ checking: true })
      Promise.resolve(guard(params))
        .then(authorized => isMounted() && setState({ checking: false, authorized }))
        .catch(error => isMounted() && setState({ checking: false, error }))
    }
  })
  /* eslint-enable react-hooks/exhaustive-deps */

  // get breadcrumbs updaters
  const { putBreadcrumb, removeBreadcrumb } = BreadcrumbsContext.use()

  // refresh breadcrumb when matched url updates
  React.useEffect(
    () => {
      if (breadcrumb) {
        putBreadcrumb(url, { ...breadcrumb, params, error })
        return () => removeBreadcrumb(url)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url, error]
  )

  // render loading view while checking
  if (checking) {
    return <LoadingView isLoading={checking} />
  }

  // render an error if no redirect was provided
  if (!redirectTo && error) {
    return <LoadingView error={error} />
  }

  // redirect if not authorized and not already on the page
  if (!authorized && redirectTo && location.pathname !== redirectTo) {
    return <Redirect to={{ pathname: redirectTo, state: { from: location } }} />
  }

  // good to go !
  return children ? (
    <Switch>
      {React.Children.map(children, child =>
        React.cloneElement(child, { path: (path === '/' ? '' : path) + (child.props.path || '') })
      )}
      {component && <ReactRouterRoute exact path={path} component={component} />}
      <ReactRouterRoute component={NotFoundView} />
    </Switch>
  ) : (
    <ReactRouterRoute {...props} />
  )
}

Route.propTypes = {
  /**
   * The component to display once the route is reached.
   */
  component: PropTypes.any,
  /**
   * The subroutes of the route.
   */
  children: PropTypes.node,
  /**
   * The breadcrumb config object.
   */
  breadcrumb: PropTypes.object,
  /**
   * A guard function (called with the route props) that must return a truthy or falsy value.
   */
  guard: PropTypes.func,
  /**
   * The location pathname where to redirect to in case of unauthorized access.
   */
  redirectTo: PropTypes.string,
}

export default React.memo(RouteWrapper)
